<template>
  <page :headStyle="'margin-top: 0.5rem'">
    <template #title>
      <scan-time :show-provider="false" :record="record" />
    </template>
    <template #rightBtn>
      <div v-if="!disableClose" @click="goBack" style="margin: -1rem; padding: 1rem;">
        <img src="@/assets/common/close-icon.svg" />
      </div>
    </template>
    <scan-processing v-if="isProcessingMobileScan" class="relative flex flex-1 flex-col" />
    <div v-else class="relative flex flex-1 flex-col">
      <model-viewer
        v-if="objToLoad && !isLoading"
        ref="modelViewer"
        bg-white
        class="fixed top-0 left-0 w-full h-full"
        :style="collapseDetail ? 'max-height: 100%' : 'max-height: calc(100% - 350px)'"
        :shader-type="shaderType"
        :obj-url="objToLoad"
        :fov="55"
        :measurement="currentMeasurement"
        :useEnvMap="true"
      />

      <div v-if="disableViewer" class="overlay fixed top-0 left-0 w-screen h-full"></div>
      <div class="detail-panel safe-area-bottom">
        <span class="toggler" @click="collapseDetail = !collapseDetail"
          ><img src="@/assets/common/icon-expand.svg" :class="{ 'can-close': !collapseDetail }"
        /></span>
        <div class="tab" :class="{ 'pb-2': collapseDetail }">
          <div
            @click="
              detailTab = 'Measurement';
              collapseDetail = false;
            "
            :class="{ active: detailTab === 'Measurement' }"
          >
            {{ $t('homePage.measurement') }}
          </div>
          <!-- <div @click="detailTab = 'Analysis'; collapseDetail = false" :class="{ 'active': detailTab === 'Analysis' }">{{ $t('homePage.analysis') }}</div> -->
          <div
            @click="
              detailTab = 'Compare';
              collapseDetail = false;
            "
            :class="{ active: detailTab === 'Compare' }"
          >
            {{ $t('homePage.comparison') }}
          </div>
        </div>
        <div
          v-show="detailTab === 'Measurement' && !collapseDetail"
          class="w-full items-center flex flex-1 flex-col"
        >
          <div class="w-full flex item-center justify-between mt-2 px-6">
            <div>
              <img src="@/assets/common/history-icon.svg" @click="showHistory = true" />
            </div>
            <unit-switcher :use-inch="useInch" :layout="'white'" @toggle="onUnitSwitch" />
          </div>
          <measurement-page
            class="mt-2"
            :measurements="measurements"
            :tags="scanTags"
            :countPerPage="5"
            :active-measurement="activeMeasurement"
            :use-inch="useInch"
            @click="onMeasurementClick"
          />
        </div>
        <div
          v-show="detailTab === 'Analysis' && !collapseDetail"
          class="p-6 w-full long overflow-y-scroll"
        >
          <analysis-page />
        </div>
        <div v-show="detailTab === 'Compare' && !collapseDetail" class="pt-6 w-full long">
          <compare-page
            :records="records"
            :tid="tid"
            :show3D="detailTab === 'Compare' && !collapseDetail"
          />
        </div>

        <!-- History -->
        <rounded-card v-if="showHistory" height="auto" no-head>
          <history
            :records="records"
            :tid="tid"
            @choose="showRecord"
            @delete="deleteRecord"
            @close="showHistory = false"
          />
        </rounded-card>
      </div>
    </div>
  </page>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { BODY_KIND, MOBILE_SCAN_PROVIDER, TG3D_MOBILE_SCAN_PROVIDER } from '@/store/modules/user';
import measurements from '@/mixins/measurements';
import ScanProcessing from '@/components/common/ScanProcessing.vue';
import ModelViewer from '@/components/common/ModelViewer.vue';
import UnitSwitcher from '@/components/common/UnitSwitcher.vue';
import RoundedCard from '@/components/common/RoundedCard.vue';
import ScanTime from '@/components/ShowResult/ScanTime.vue';
import History from '@/components/ShowResult/History.vue';
import Page from './Page.vue';
import MeasurementPage from './MeasurementPage.vue';
import AnalysisPage from './AnalysisPage.vue';
import ComparePage from './ComparePage.vue';

export default {
  mixins: [measurements],
  components: {
    Page,
    ScanProcessing,
    ModelViewer,
    UnitSwitcher,
    MeasurementPage,
    AnalysisPage,
    ComparePage,
    RoundedCard,
    ScanTime,
    History,
  },
  props: {
    record: Object,
    disableClose: Boolean,
  },
  data: () => ({
    detailTab: 'Measurement',
    showFailedPrompt: false,
    isLoading: false,
    showHistory: false,
    collapseDetail: false,
    activeMeasurement: null,
  }),
  methods: {
    ...mapActions('user', ['setObjUrlByTID', 'deleteScanRecord', 'getSizeXt']),
    showRecord(record) {
      this.$emit('show-record', record);
      this.activeMeasurement = null;
      this.showHistory = false;
    },
    goBack() {
      this.$emit('back');
    },
    async deleteRecord(tid) {
      if (!tid) return;

      this.$events.startLoading();
      try {
        const isDeletingCurrent = tid === this.tid;
        const previousIndex = this.records.findIndex(r => r.tid === tid);
        await this.deleteScanRecord({ tid, kind: BODY_KIND });
        const newRecords = this.records;
        if (newRecords.length === 0) {
          // if no record available, go back to home page and make sure it's blank
          this.goBack();
        } else if (isDeletingCurrent) {
          // if the current one is deleted, try choosing the nearest (older) one
          this.$emit('show-record', this.records[previousIndex]);
        }
      } catch (e) {
        this.$events.alert(this.$t('welcome.unexpected_error'));
        console.log(`[ERROR] Failed to delete scan record: ${tid}, ${e?.message}`);
      } finally {
        this.$events.stopLoading();
      }
    },
    async getMeasurements() {
      try {
        const sizext = await this.getSizeXt({ tid: this.tid, refresh: true });
        this.processMeasurements(sizext, true, false, this.customMeasurements);
      } catch (e) {
        this.$events.alert(this.$t('welcome.unexpected_error'));
        console.log(e.message);
      }
    },
    onMeasurementClick(m) {
      this.activeMeasurement = m === this.activeMeasurement ? null : m;
      this.setCurrentMeasurement(this.activeMeasurement);
    },
    resizeViewer() {
      if (!this.$refs.modelViewer) return;
      setTimeout(() => {
        this.$refs.modelViewer.onWindowResize();
      }, 1);
    },
  },
  computed: {
    ...mapState('app', ['scannersConfigs']),
    ...mapState('user', ['bodyRecords', 'objUrl', 'mobileScanObjUrl']),
    records() {
      return this.bodyRecords;
    },
    objToLoad() {
      return this.isMobileScan ? this.mobileScanObjUrl : this.objUrl;
    },
    tid() {
      const { tid = '' } = this.record || {};
      return tid;
    },
    scannerId() {
      const { uuid = '' } = this.record.scanner || {};
      return uuid;
    },
    customMeasurements() {
      if (this.scannerId && this.scannersConfigs[this.scannerId]) {
        return this.scannersConfigs[this.scannerId].custom_measurements;
      }
      return null;
    },
    scanTags() {
      const { tag_list: tags = [] } = this.record || {};
      return tags;
    },
    shaderType() {
      let type = 'default';
      if (this.scannerId && this.scannersConfigs[this.scannerId]) {
        type = this.scannersConfigs[this.scannerId].shader_type || 'default';
      }

      return type;
    },
    isMobileScan() {
      const { provider = 0 } = this.record || {};
      return provider === MOBILE_SCAN_PROVIDER || provider === TG3D_MOBILE_SCAN_PROVIDER;
    },
    isProcessingMobileScan() {
      const { status = 99 } = this.record || {};
      return this.isMobileScan && status < 3;
    },
    disableViewer() {
      return !this.collapseDetail && this.detailTab !== 'Measurement';
    },
  },
  watch: {
    measurementFailed: {
      handler(nv) {
        this.showFailedPrompt = nv;
      },
      immediate: true,
    },
    objToLoad: {
      handler(nv) {
        if (!nv && !this.isLoading) {
          this.measurementFailed = true;
        }
      },
      immediate: true,
    },
    tid: {
      async handler(nv) {
        this.showFailedPrompt = false;
        this.measurementFailed = false;
        this.$events.startLoading();
        this.isLoading = true;
        console.log(`[INFO] tid: ${nv}, tags: ${this.scanTags}`);
        if (nv && !this.isProcessingMobileScan) {
          await this.getMeasurements();
        }
        await this.setObjUrlByTID(this.record);
        this.isLoading = false;
        this.$events.stopLoading();
        if (!this.objToLoad) {
          console.error('objUrl or mobileScanObjUrl is null.');
          this.$events.alert(this.$t('welcome.unexpected_error'));
        }
      },
      immediate: true,
    },
    collapseDetail: {
      handler() {
        this.resizeViewer();
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.overlay {
  z-index: 1;
  background-color: #262626cc;
}

.detail-panel {
  @apply fixed bottom-0 flex flex-col w-full pt-2;
  z-index: 2;
  align-items: center;
  background: #ffffff;
  box-shadow: 0px -9px 10px rgba(78, 78, 78, 0.2);
  border-radius: 20px 20px 0px 0px;

  .short {
    min-height: 350px;
    max-height: 350px;
  }

  .long {
    height: 80vh;
    height: calc(80vh - env(safe-area-inset-bottom) - env(safe-area-inset-top));
  }

  @supports (height: 100svh) {
    .long {
      height: 85svh;
      height: calc(85svh - env(safe-area-inset-bottom) - env(safe-area-inset-top));
    }
  }

  .toggler {
    @apply flex absolute;
    transform: translateY(-3rem);
    width: 36px;
    height: 28px;
    background: url('../../../assets/common/icon-expand.svg') center no-repeat;
    background: #ffffff;
    border-radius: 100px;
    cursor: pointer;

    img {
      margin: auto;
      transform: scaleY(-1);
    }

    .can-close {
      transform: scaleY(1);
    }
  }

  .tab {
    @apply flex flex-row w-full justify-around pt-1;
    color: #2626264c;
    padding-left: 1.5rem;
    padding-right: 1.5rem;

    .active {
      background: linear-gradient(270deg, #2245ff 0.85%, #4300d9 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      text-fill-color: transparent;
    }
  }
}
</style>
